import { useEffect, useState } from 'react';
import { Badge, Skeleton } from 'antd';
import { PresetStatusColorType } from 'antd/lib/_util/colors';

import { useLazyVacationsQuery, useVacationsQuery } from 'app/services/edpApiV1/vacations';
import { TableInProgress } from 'features/tables/tableInProgress';

import { dataForVacationTable, getZupStatusName, sortingDate } from 'shared/helpers/utils';
import { RequestsSearch, TColumns } from 'shared/types/types';
import { SortCondition } from 'shared/enums';
import { usePullSort } from 'shared/hooks';
import SuperviserApproveMarker from 'shared/components/superviserApproveMarker';
import { useIndicators } from 'features/indicators';
import { ONE_MINUTE } from 'shared/constants';
import { TPreparedVacationInfoData } from 'shared/types/vacations.types';
import { SearchKeysNews } from 'shared/enums/news';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppSelector } from 'app/store';
import { DayOffsStatusTypesMap } from 'shared/types/dayoff.types';

export const VacationInProgress = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataVacations, setDataVacations] = useState<TPreparedVacationInfoData[] | []>([]);
  const [sortInfo, trigger] = usePullSort();
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const [getVacations, { data, isSuccess, isFetching, isLoading }] = useLazyVacationsQuery({
    pollingInterval: ONE_MINUTE,
  });

  useEffect(() => {
    getVacations({
      type: 'my',
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getVacations, search, sortInfo.value, sortOption, allAvailableOrganizations]);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
    },
    {
      title: 'Шаг согласования',
      dataIndex: 'applyStep',
      key: 'applyStep',
    },
    {
      title: 'Тип заявления',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: (a, b) => sortingDate(a.startedAt, b.startedAt),
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
      sorter: (a, b) => sortingDate(a.endedAt, b.endedAt),
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (
        status: { type?: PresetStatusColorType; text: string; isSupervisorApproved: boolean; steps: number | null },
        record,
      ) => {
        return (
          <>
            <span className="d-flex">
              <Badge status={DayOffsStatusTypesMap[status.type || 'processing']} />
              {status.text}
            </span>
            {(() => {
              if ('zupRequestStatus' in record && record.zupRequestStatus) {
                return (
                  <>
                    <br />
                    <span className="d-flex">
                      <Badge status={record.zupRequestStatus.type} />
                      {getZupStatusName(record.zupRequestStatus.type)}
                    </span>
                  </>
                );
              }
            })()}
            {status.steps && !status.isSupervisorApproved && (
              <>
                <br />
                <SuperviserApproveMarker text="Не согласовано руководителем" />
              </>
            )}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (data) {
      const dataTable = dataForVacationTable(data?.data);
      setDataVacations(dataTable);
    }
  }, [data]);
  // подписываемся на обновление индикаторов, после обновления списка и отключаем пуллинг звпроса индикаторов
  useIndicators(isSuccess, isFetching, false);

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          {dataVacations && columns && (
            <TableInProgress
              dataSource={dataVacations}
              columns={columns}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              currentSize={currentSize}
              setCurrentSize={setCurrentSize}
              total={data?.pagination?.total}
              onSearch={onSearch}
            />
          )}
        </>
      )}
    </>
  );
};
